
import html2canvas from 'html2canvas';
import { supabase } from "@/integrations/supabase/client";

export const generatePreview = async (planId: string): Promise<void> => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  // Vergrote iframe dimensies voor meer ruimte om de plattegrond weer te geven
  iframe.style.width = '3000px'; // Increased from 2000px
  iframe.style.height = '3000px'; // Increased from 2000px
  iframe.style.pointerEvents = 'none';
  document.body.appendChild(iframe);

  try {
    const timestamp = Date.now();
    const url = `${window.location.origin}/?id=${planId}&preview=true&t=${timestamp}`;
    iframe.src = url;

    // Wacht tot de iframe is geladen
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout bij laden van de iframe'));
      }, 20000); // Increased from 15 seconds to 20 seconds
      
      iframe.onload = () => {
        clearTimeout(timeout);
        resolve();
      };
      
      iframe.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Fout bij laden van de iframe'));
      };
    });

    // Geef extra tijd voor het renderen van de inhoud
    await new Promise(resolve => setTimeout(resolve, 3000)); // Increased from 2000ms to 3000ms

    const iframeDocument = iframe.contentWindow?.document;
    if (!iframeDocument) {
      throw new Error('Kan geen toegang krijgen tot iframe document');
    }

    const flowContainer = iframeDocument.querySelector('.react-flow');
    if (!flowContainer) {
      throw new Error('Kan react-flow container niet vinden in de iframe');
    }

    const flowInstance = (flowContainer as any).__reactProps$?.flowRef?.current;
    if (flowInstance) {
      // Verbeterde fitView configuratie met meer padding voor betere zichtbaarheid van alle elementen
      flowInstance.fitView({ padding: 0.3, duration: 0 }); // Increased padding from 0.25 to 0.3
      // Wacht even tot de view is aangepast
      await new Promise(resolve => setTimeout(resolve, 1000)); // Increased from 800ms to 1000ms
    }

    // Probeer de react-flow__viewport te vinden voor een nauwkeurigere capture
    const targetElement = iframeDocument.querySelector('.react-flow__viewport') || 
                          iframeDocument.querySelector('.react-flow__renderer') || 
                          iframeDocument.querySelector('.react-flow');
    
    if (!targetElement) {
      throw new Error('Kan geen renderelement vinden in de iframe');
    }

    console.log('Generating screenshot of element:', targetElement);
    const screenshot = await html2canvas(targetElement as HTMLElement, {
      backgroundColor: '#ffffff',
      scale: 2.5, // Increased from 2 to 2.5 for better resolution
      logging: false,
      // Zorg ervoor dat alle content binnen de grenzen valt
      width: (targetElement as HTMLElement).scrollWidth,
      height: (targetElement as HTMLElement).scrollHeight,
      // Negeer UI-besturingselementen die niet tot de plattegrond zelf behoren
      ignoreElements: (element) => {
        return element.classList?.contains('react-flow__controls') ||
               element.classList?.contains('react-flow__minimap') ||
               element.classList?.contains('react-flow__background') ||
               element.classList?.contains('react-flow__panel') ||
               element.classList?.contains('react-flow__handle');
      },
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: true // Added to better handle complex elements
    });

    const imageData = screenshot.toDataURL('image/png', 1.0);
    
    console.log('Preview generated, updating database...');
    const { error } = await supabase
      .from('floor_plans')
      .update({ 
        preview_image: imageData,
        updated_at: new Date().toISOString()
      })
      .eq('id', planId);

    if (error) {
      console.error('Error updating preview in database:', error);
      throw error;
    }

    console.log('Preview updated in database successfully');

    // Update preview in UI als we op de dashboard pagina zijn
    const previewImg = document.querySelector(`[data-preview-id="${planId}"]`) as HTMLImageElement;
    if (previewImg) {
      console.log('Updating preview image in UI');
      previewImg.src = imageData + '?t=' + Date.now();
    }

  } catch (error) {
    console.error('Error in generatePreview:', error);
    throw error;
  } finally {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }
};
